import React, {PropsWithChildren, useRef, useState} from 'react';
import AlertContext, {VisaErrorOptions} from './AlertContext';
import Modal from 'react-bootstrap/Modal';
import {WebSvg} from '@/assets';
import {Button} from '@/components/common';

interface Props extends PropsWithChildren {}

interface Config {
  message: string;
  description: string;
  data: Record<string,any>;
  onClick: () => void;
  type: string;
  errorType: string;
  fields: string[];
  errors: string[];
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  hideSecondary?: boolean;
  onDismiss?: () => void;
}

const AlertProvider = ({children}: Props) => {
  const [visible, setVisible] = useState(false);
  const config = useRef<Config>({
    message: '',
    description: '',
    data: {},
    onClick: () => {},
    type: '',
    errorType: '',
    fields: [],
    errors: [],
    secondaryLabel: undefined,
    onSecondaryClick: undefined,
    hideSecondary: false,
  });

  const success = (message: string, onClick: () => void) => {
    config.current = {
      message,
      data: {},
      onClick,
      description: '',
      type: 'success',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };

  const confirm = (message: string, onClick: () => void) => {
    config.current = {
      message,
      data: {},
      onClick,
      description: '',
      type: 'confirm',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };

  const error = (message: string) => {
    config.current = {
      message,
      data: {},
      onClick: () => {},
      description: '',
      type: 'error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };

  const visaError = (
    message: string,
    description: string,
    onClick: () => void,
    options: VisaErrorOptions = {},
  ) => {
    config.current = {
      message,
      data: {},
      description,
      onClick,
      type: 'visa_error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: options.secondaryLabel,
      onSecondaryClick: options.onSecondaryClick,
      hideSecondary: options.hideSecondary ?? false,
    };
    setVisible(true);
  };
  const passportError = (
    errorType: string,
    fields: string[],
    errors: string[] = [],
    options: VisaErrorOptions = {},
  ) => {
    config.current = {
      message: '',
      data: {},
      description: '',
      onClick: () => {},
      type: 'passport_error',
      errorType,
      fields,
      errors,
      secondaryLabel: options.secondaryLabel,
      onSecondaryClick: options.onSecondaryClick,
      hideSecondary: options.hideSecondary ?? false,
    };
    setVisible(true);
  };
  const passportNumberError = (
    message: string,
    description: string,
    onClick: () => void,
    options: VisaErrorOptions = {},
  ) => {
    config.current = {
      message,
      data: {},
      description,
      onClick,
      type: 'passport_number_error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: options.secondaryLabel,
      onSecondaryClick: options.onSecondaryClick,
      hideSecondary: options.hideSecondary ?? false,
      onDismiss: options.onDismiss,
    };
    setVisible(true);
  };
  const tenantAdditionalError = (
    message: string,
    description: string,
    onClick: () => void,
  ) => {
    config.current = {
      message,
      data: {},
      description,
      onClick,
      type: 'tenant_additional_error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };
  const tenantEditDetailError = (
    message: string,
    data: Record<string,any>,
    onClick: () => void,
  ) => {
    config.current = {
      message,
      description: "",
      data,
      onClick,
      type: 'tenant_edit_detail_error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };
  const tenantStatementError = (
    message: string,
    description: string,
    onClick: () => void,
  ) => {
    config.current = {
      message,
      data: {},
      description,
      onClick,
      type: 'tenant_statement_error',
      errorType: '',
      fields: [],
      errors: [],
      secondaryLabel: undefined,
      onSecondaryClick: undefined,
      hideSecondary: false,
    };
    setVisible(true);
  };

  const close = () => setVisible(false);

  const onConfirm = () => {
    config.current.onClick();
    close();
  };

  const onSecondary = () => {
    config.current.onSecondaryClick?.();
    close();
  };

  const successBody = () => (
    <React.Fragment>
      <div className="popup-icon d-flex justify-content-center mt-4">
        <img src={WebSvg.GuideIcon} />
      </div>
      <div className="statement-text mt-4 green-text">
        {config.current.message}
      </div>
      <div className="d-flex justify-content-center my-4">
        <Button
          onClick={onConfirm}
          type="submit"
          variant="primary"
          className="mt-2 "
        >
          Continue
        </Button>
      </div>
    </React.Fragment>
  );

  const errorBody = () => (
    <React.Fragment>
      <div className="statement-text mt-4">{config.current.message}</div>
      <div className="d-flex justify-content-center my-4">
        <Button
          onClick={close}
          type="submit"
          variant="primary"
          className="mt-2 "
        >
          I understand
        </Button>
      </div>
    </React.Fragment>
  );

  const passportErrorBody = () => (
    <React.Fragment>
      <div className="d-flex flex-column text-center">
        <p className="fw-medium fs-5 mt-1 px-3 text-start">
          Fields marked with * are required. The following fields could not be
          read:
          <span className="fw-semibold" style={{color: 'red'}}>
            {' '}
            {config.current.fields.join(', ')}.
          </span>
        </p>
        <p className="fs-5 px-3" style={{textAlign: 'left'}}>
          Please upload a high-resolution passport {config.current.errorType}{' '}
          image in JPEG or JPG format.
        </p>
        {config.current.errors && config.current.errors.length > 0 && (
          <ul className="fw-medium fs-5 mt-1 text-start" style={{color: 'red'}}>
            {config.current.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
        <p className="fs-6 mt-3">
          Need help? Watch this guide:
          <a
            href="https://drive.google.com/file/d/1eEBLQgnW-yIKUenoYX4RRMO_betzsj5s/preview"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary fw-semibold"
          >
            {' '}
            How to upload your passport
          </a>
        </p>
        <div className="d-flex justify-content-center my-2 gap-3">
          {!config.current.hideSecondary && config.current.onSecondaryClick && (
            <Button onClick={onSecondary} type="submit" variant="secondary">
              {config.current.secondaryLabel || 'Retry'}
            </Button>
          )}
          <Button onClick={close} type="submit" variant="primary">
            I understand
          </Button>
        </div>
      </div>
    </React.Fragment>
  );

  const confirmBody = () => (
    <Modal show={visible} onHide={close}>
      <Modal.Header>
        <Modal.Title>{config.current.message}</Modal.Title>
      </Modal.Header>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const passportNumberErrorBody = () => (
    <Modal
      show={visible}
      onHide={() => {
        config.current.onDismiss?.();
        close();
      }}
    >
      <Modal.Header>
        <Modal.Title>{config.current.message}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{config.current.description}</Modal.Body>
      <Modal.Footer>
        {!config.current.hideSecondary && (
          <Button variant="secondary" onClick={onSecondary}>
            {config.current.secondaryLabel || 'Skip'}
          </Button>
        )}
        <Button variant="primary" onClick={onConfirm}>
          Re-Upload
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const visaErrorBody = () => (
    <Modal show={visible} onHide={close}>
      <Modal.Header>
        <Modal.Title>{config.current.message}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{config.current.description}</Modal.Body>
      <Modal.Footer>
        {!config.current.hideSecondary && (
          <Button variant="secondary" onClick={onSecondary}>
            {config.current.secondaryLabel || 'Ignore'}
          </Button>
        )}
        <Button variant="primary" onClick={onConfirm}>
          Re-Upload
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const tenantAdditionalErrorBody = () => (
    <React.Fragment>
      <div className="statement-text mt-4">{config.current.message}</div>
      <div className="d-flex flex-column flex-md-row justify-content-center my-4 gap-4">
      <Button variant="secondary" onClick={close}>
         I want to Upload again
       </Button>
       <Button variant="primary" onClick={onConfirm}>
         Yes I want to proceed
       </Button>
      </div>
    </React.Fragment>
  );

  const tenantEditDetailErrorBody = () => (
    <Modal show={visible} onHide={close} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          <div style={{fontSize: '22px', fontWeight: 'bold'}}>
            {config.current.message}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div><span style={{fontSize: '18px', fontWeight: '500'}}>Full name:</span> <span style={{fontSize: '16px'}}>{config.current.data.full_name}</span></div>
        <div className='mt-1'><span style={{fontSize: '18px', fontWeight: '500'}}>Phone number:</span> <span style={{fontSize: '16px'}}>{config.current.data.phone_number}</span></div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          Yes, proceed
        </Button>
        <Button variant="primary" onClick={() => {
          onConfirm();
          close();
        }}>
          No, I want to edit
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const tenantStatementErrorBody = () => (
    <Modal show={visible} onHide={close} size="lg" centered={true}  backdrop="static" keyboard={false}>
      <Modal.Body>
      <div className="d-flex flex-column w-100">
        <div className='statement-text mt-4'>{config.current.message}</div>
        <div className='d-flex flex-column flex-md-row justify-content-center my-4 gap-4'>
          <Button variant='secondary' onClick={() => {
            onConfirm();
            close();
          }}>
            I want to Upload again
          </Button>
          <Button variant='primary' onClick={close}>
            Yes I want to proceed
          </Button>
        </div>
      </div>
      </Modal.Body>
    </Modal>
  )

  return (
    <AlertContext.Provider
      value={{success, error, confirm, visaError, passportError, passportNumberError, tenantAdditionalError, tenantEditDetailError, tenantStatementError}}
    >
      {children}
      {config.current.type === 'confirm' ? (
        confirmBody()
      ) : config.current.type === 'visa_error' ? (
        visaErrorBody()
      ) : config.current.type === 'passport_number_error' ? (
        passportNumberErrorBody()
      ) : config.current.type === 'tenant_edit_detail_error' ? (
        tenantEditDetailErrorBody()
      ) : config.current.type === 'tenant_statement_error' ? (
        tenantStatementErrorBody()
      )  : (
        <Modal
          show={visible}
          onHide={() => setVisible(false)}
          size="lg"
          centered={true}
        >
          <Modal.Body>
            <div className="d-flex flex-column w-100">
              {config.current.type === 'success'
                ? successBody()
                : config.current.type === 'passport_error'
                  ? passportErrorBody()
                : config.current.type === 'tenant_additional_error'
                  ? tenantAdditionalErrorBody()
                : errorBody()}
            </div>
          </Modal.Body>
        </Modal>
      )}
    </AlertContext.Provider>
  );
};

export default AlertProvider;
